/* Shared per-user progress helpers.
 *
 * Currently backed by localStorage since auth isn't wired. When auth lands,
 * swap the storage layer here and every screen that consumes these helpers
 * stays in sync automatically.
 *
 * Keys:
 *   medily.progress.<guideSlug>.<topicSlug>    = "1"       (completion flag)
 *   medily.progress.tips.<tipSlug>             = "1"       (completion flag)
 *   medily.progress.meta.<key>                 = JSON meta  (title/date/href)
 */

const META_PREFIX = "medily.progress.meta.";
const COMPLETION_PREFIX = "medily.progress.";

export const setCompletion = (storageKey, meta) => {
  try {
    localStorage.setItem(storageKey, "1");
    if (meta) {
      localStorage.setItem(
        `${META_PREFIX}${storageKey}`,
        JSON.stringify({ at: new Date().toISOString(), ...meta })
      );
    }
    window.dispatchEvent(new Event("medily.progress.change"));
  } catch (_) {}
};

export const clearCompletion = (storageKey) => {
  try {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(`${META_PREFIX}${storageKey}`);
    window.dispatchEvent(new Event("medily.progress.change"));
  } catch (_) {}
};

export const isCompleted = (storageKey) => {
  try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
};

/* All completed items with their metadata, newest first. */
export const listRecentCompletions = () => {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(META_PREFIX)) continue;
      const raw = localStorage.getItem(k);
      try {
        const meta = JSON.parse(raw);
        const target = k.slice(META_PREFIX.length);
        // Only include entries whose completion flag is still set
        if (localStorage.getItem(target) !== "1") continue;
        out.push({ key: target, ...meta });
      } catch (_) {}
    }
  } catch (_) {}
  out.sort((a, b) => new Date(b.at) - new Date(a.at));
  return out;
};

/* Count of completed topics / tips separately (used by readiness). */
export const countCompletions = () => {
  const prefix = COMPLETION_PREFIX;
  let topics = 0;
  let tips = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      if (k.startsWith(META_PREFIX)) continue;
      if (localStorage.getItem(k) !== "1") continue;
      if (k.startsWith(`${prefix}tips.`)) tips += 1;
      else topics += 1;
    }
  } catch (_) {}
  return { topics, tips };
};

/* Interview Readiness — same weighting used by Dashboard + Progress so the
 * number is identical across screens. */
export const calcReadiness = ({ totalTopics, totalTips, mayaUsed, mayaGoal = 15 }) => {
  const { topics: doneTopics, tips: doneTips } = countCompletions();
  const weights = { study: 0.5, tips: 0.2, maya: 0.3 };
  const studyPct = totalTopics ? Math.min(1, doneTopics / totalTopics) : 0;
  const tipsPct = totalTips ? Math.min(1, doneTips / totalTips) : 0;
  const mayaPct = Math.min(1, (mayaUsed || 0) / mayaGoal);
  const total = studyPct * weights.study + tipsPct * weights.tips + mayaPct * weights.maya;
  return Math.round(total * 100);
};
